import { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import {
  ErrorBoundary,
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType,
  GrcDrawer,
  GrcDrawerOpenedStateMode,
  GrcDrawerPanelLocation,
  GrcDrawerRevealMode,
  GrcDropDownButton,
  GrcDropDownButtonStylingMode,
  GrcSkeleton,
  GrcSkeletonVariant
} from '@grc/ui-package';
import Tenants from './pages/tenants/tenants';
import AppNav, { NAV_PANEL_WIDTH, NAV_RAIL_WIDTH } from './app-nav';
import 'devextreme/dist/css/dx.light.css';
import './app.scss';

// Split out: the usage screen pulls in the DevExtreme charting bundle (~180 kB gzipped), which
// no other page needs. Loading it on demand keeps the tenants list from paying for it.
const TenantUsage = lazy(() => import('./pages/usage/tenant-usage'));

const REQUIRED_PERMISSION = 'platformadmin:tenant-admin';

function decodePermissions(accessToken: string): string[] {
  try {
    const payload = accessToken.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    return Array.isArray(json.permissions) ? json.permissions : [];
  } catch {
    return [];
  }
}

/** Shaped like the grid that follows it, so the page does not jump when rows arrive. */
const GridSkeleton = () => (
  <GrcSkeleton variant={GrcSkeletonVariant.Rectangular} count={6} height={44} />
);

function AppShell() {
  const { user, logout, isLoading, getAccessTokenSilently } = useAuth0();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [navOpen, setNavOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const permissions = decodePermissions(token);
        if (!cancelled) setHasAccess(permissions.includes(REQUIRED_PERMISSION));
      } catch {
        if (!cancelled) setHasAccess(false);
      } finally {
        if (!cancelled) setAccessChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const signOut = () => logout({ logoutParams: { returnTo: window.location.origin } });

  // The toggle is only offered where there is a drawer to toggle — the access states
  // below render the header without one.
  const renderHeader = (withNavToggle: boolean) => (
    <header className="app-header">
      <div className="app-header-start">
        {withNavToggle && (
          <GrcButton
            id="app-nav-toggle"
            icon="menu"
            type={GrcButtonType.Normal}
            stylingMode={GrcButtonStylingMode.Text}
            onClick={() => setNavOpen(open => !open)}
          />
        )}
        <h1>Platform Admin</h1>
      </div>
      <div className="user-info">
        {isLoading && <GrcSkeleton variant={GrcSkeletonVariant.Text} width={180} />}
        {!isLoading && user && (
          <GrcDropDownButton
            id="app-user-menu"
            text={user.email ?? user.name ?? ''}
            icon="user"
            items={[{ id: 'logout', text: 'Log out' }]}
            keyExpr="id"
            displayExpr="text"
            stylingMode={GrcDropDownButtonStylingMode.Text}
            onItemClick={e => {
              if ((e.itemData as { id?: string }).id === 'logout') signOut();
            }}
          />
        )}
      </div>
    </header>
  );

  if (!accessChecked) {
    return (
      <div className="platform-admin-app">
        {renderHeader(false)}
        <div className="app-view">
          <GridSkeleton />
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="platform-admin-app">
        {renderHeader(false)}
        <div className="app-view">
          <div className="access-denied">
            <h2>Access denied</h2>
            <p>
              Your account is signed in, but it doesn't have the
              <code> {REQUIRED_PERMISSION} </code>
              permission required to use Platform Admin.
            </p>
            <p>Contact an administrator to get access, or log out to try a different account.</p>
            <div className="access-denied-actions">
              <GrcButton
                id="access-denied-logout"
                text="Log out"
                type={GrcButtonType.Default}
                stylingMode={GrcButtonStylingMode.Contained}
                onClick={signOut}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-admin-app">
      {renderHeader(true)}
      <div className="app-shell">
        {/*
          GrcDrawer renders its own children as an empty div, so the routed view cannot
          live inside it: the panel comes in through `component` and the view area is a
          sibling. The rail column travels with the panel, which the drawer animates
          between minSize and maxSize over its default 400ms.
        */}
        <div
          className={navOpen ? 'app-nav' : 'app-nav app-nav-collapsed'}
          style={{ width: navOpen ? NAV_PANEL_WIDTH : NAV_RAIL_WIDTH }}
        >
          <GrcDrawer
            id="app-nav"
            opened={navOpen}
            openedStateMode={GrcDrawerOpenedStateMode.Shrink}
            position={GrcDrawerPanelLocation.Left}
            // Expand, not Slide: a slid rail shows the panel's trailing edge mid-word.
            revealMode={GrcDrawerRevealMode.Expand}
            minSize={NAV_RAIL_WIDTH}
            maxSize={NAV_PANEL_WIDTH}
            component={AppNav}
          />
        </div>

        <main className="app-view">
          <Routes>
            <Route path="/" element={<Navigate to="/tenants" replace />} />
            <Route
              path="/tenants"
              element={
                <ErrorBoundary componentName="Tenants">
                  <Tenants />
                </ErrorBoundary>
              }
            />
            <Route
              path="/tenants/:tenantId/usage"
              element={
                <ErrorBoundary componentName="Tenant usage">
                  <Suspense
                    fallback={
                      <GrcSkeleton variant={GrcSkeletonVariant.Rectangular} height={340} />
                    }
                  >
                    <TenantUsage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default withAuthenticationRequired(AppShell, {
  onRedirecting: () => <div className="platform-admin-app">Redirecting to login…</div>
});
